import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { Link } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable, DropResult, DroppableProvided } from 'react-beautiful-dnd';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import DisplayDate from '../../components/DisplayDate';
import Button from '../../components/Button';
import StatusIcon from '../../components/StatusIcon';
import DisplayDragIcon from '../../components/DisplayDragIcon';
import LoadingSpinner from '../../components/LoadingSpinner';
import NoInfoMessage from '../../components/NoInfoMessage';
import GlobalModal from '../../components/GlobalModal';
import RemoveModal from './RemoveModal';

// ICONS
import { ReactComponent as PlusIcon } from '../../assets/svg/plus.svg';

// TYPES
import { ChecklistTemplateDashboardTemplate, ChecklistTemplateReorderCommand } from '../../api/models';

const ContentChecklistTemplates: React.FC = () => {
	// Check if Organisation is active
	const isOrganisationActive = useAppSelector((state) => state.user.isOrganisationActive);

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// State to control Modal
	const [showRemoveModal, setShowRemoveModal] = React.useState<boolean>(false);

	// State to store Template Id
	const [templateId, setTemplateId] = React.useState<string>('');

	// State to store Child Safe Templates title
	const [templateTitle, setTemplateTitle] = React.useState<string>('');

	// Set up Data state
	const [safetyChecklistsTemplatesData, setSafetyChecklistsTemplatesData] = React.useState<
		ChecklistTemplateDashboardTemplate[]
	>([]);

	const getSafetyChecklistsTemplatesData = async () => {
		setIsLoading(true);

		const response = await apibridge.getChecklistTemplateDashboard();

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = [] } = response;

			setIsLoading(false);

			setSafetyChecklistsTemplatesData(result);
		}
	};

	React.useEffect(() => {
		getSafetyChecklistsTemplatesData();
	}, []);

	// Handling when we drag
	const onDragEndHandler = (result: DropResult) => {
		if (!result.destination) return;

		const items = Array.from(safetyChecklistsTemplatesData);

		const [reorderedItem] = items.splice(result.source.index, 1);

		items.splice(result.destination.index, 0, reorderedItem);

		setSafetyChecklistsTemplatesData(items);

		const data: ChecklistTemplateReorderCommand = { id: reorderedItem.id, order: result.destination.index + 1 };

		apibridge.postChecklistTemplateReorder(data);
	};

	const removeHandler = async (item: ChecklistTemplateDashboardTemplate) => {
		setTemplateId(item.id || '');

		setTemplateTitle(item.title || '');

		setShowRemoveModal(true);
	};

	return (
		<div className="page-general-layout">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<div className="single-btn-right-container border">
									<Link className="btn-primary btn-right" to="/content-checklist-templates-edit">
										<PlusIcon className="icon-link" /> Add new
									</Link>
								</div>

								<section className="info-section mtop-0">
									{safetyChecklistsTemplatesData && safetyChecklistsTemplatesData.length > 0 ? (
										<DragDropContext onDragEnd={onDragEndHandler}>
											<Droppable droppableId="trainingModulesItem">
												{(provided: DroppableProvided) => (
													<ul {...provided.droppableProps} ref={provided.innerRef}>
														{safetyChecklistsTemplatesData.map((item, index) => {
															return (
																<Draggable
																	key={index}
																	draggableId={index.toString()}
																	index={index}
																	isDragDisabled={isOrganisationActive === false}
																>
																	{(provided) => (
																		<li
																			className="mbtom-16 "
																			ref={provided.innerRef}
																			{...provided.draggableProps}
																			{...provided.dragHandleProps}
																		>
																			<div className="row detail-row">
																				<div className="col-auto pdl-0 pdr-0">
																					<StatusIcon statusType="document" />
																				</div>

																				<div className="col-md-auto full-width">
																					<div className="row detail-info">
																						<div className="col-10 col-md-6 detail-name">{item.title}</div>

																						<div className="col-10 col-md-6 text-category-detail">
																							Last updated &nbsp; &nbsp;
																							<span className="text-subtitle">
																								<DisplayDate>{item.updated}</DisplayDate>
																							</span>
																						</div>
																					</div>

																					<div className="d-block d-sm-none">
																						<Link
																							className="btn-text remove link"
																							to={`/content-checklist-templates-edit/${item.id}`}
																						>
																							Edit
																						</Link>

																						<Button className="btn-text link pdr-0" onClick={() => removeHandler(item)}>
																							Remove
																						</Button>
																					</div>
																				</div>

																				<div className="col-auto pdr-0 link-group content-training-module-page">
																					<div className="d-none d-sm-flex">
																						<Link
																							className="btn-text remove link"
																							to={`/content-checklist-templates-edit/${item.id}`}
																						>
																							Edit
																						</Link>

																						<Button className="btn-text link pdr-0" onClick={() => removeHandler(item)}>
																							Remove
																						</Button>
																					</div>

																					<div className="drag-icon-container">
																						<DisplayDragIcon index={index} array={safetyChecklistsTemplatesData} />
																					</div>
																				</div>
																			</div>
																		</li>
																	)}
																</Draggable>
															);
														})}
														{provided.placeholder}
													</ul>
												)}
											</Droppable>
										</DragDropContext>
									) : (
										<NoInfoMessage name="Checklist templates" />
									)}
								</section>
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}

			<GlobalModal isOpen={showRemoveModal} setIsOpen={setShowRemoveModal} titleText="Remove template">
				<RemoveModal
					safetyChecklistsTemplatesData={safetyChecklistsTemplatesData}
					setSafetyChecklistsTemplatesData={setSafetyChecklistsTemplatesData}
					getSafetyChecklistsTemplatesData={getSafetyChecklistsTemplatesData}
					templateId={templateId}
					templateTitle={templateTitle}
				/>
			</GlobalModal>
		</div>
	);
};

export default ContentChecklistTemplates;
